"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { ClipboardCheck, Loader2 } from "lucide-react";

const EQR_REVIEW_FILE = "EQRReview.json";

const EQR_QUESTIONS = [
  {
    id: "EQR-1",
    text: "Have you performed a review of the engagement team's competence and independence in relation to the specific engagement?",
  },
  {
    id: "EQR-2",
    text: "Have you reviewed the materiality calculated by the engagement team, including the appropriateness of the benchmarks considered based on the factors for the benchmark, and concluded the same to be appropriate?",
  },
  {
    id: "EQR-3",
    text: "Have you assessed the significant risks identified by the engagement team, judgements made and the responses to the identified risks and found no issues in the same? In evaluating the significant judgements and conclusions, the following can be performed:\n- Discussion of significant matters with engagement partner;\n- Review of the financial statements and proposed auditor's report;\n- Review selected audit documentation relating to significant judgements and conclusions reached;\n- Evaluating conclusions reached in preparing audit report and whether the proposed audit report is appropriate.",
  },
  {
    id: "EQR-4",
    text: "Where any consultations were required on difficult or contentious matters or differences of opinion within the engagement team or where applicable between the engagement partner and engagement quality control reviewer, whether they have taken place and report is issued only after such matters are resolved?",
  },
  {
    id: "EQR-5",
    text: "Have you reviewed that the misstatements identified during the engagement (corrected and uncorrected) have been disposed in accordance with the Standards on Auditing and whether the same, whether individually or in aggregate, cause the FS to be materially misstated?",
  },
  {
    id: "EQR-6",
    text: "Have you reviewed the matters that are to be communicated with the management and those charged with governance, and wherever applicable, others like regulatory bodies and that these cover all relevant matters to be communicated in accordance with the Standards on Auditing?",
  },
  {
    id: "EQR-7",
    text: "Have you ascertained whether working papers selected for review and other audit evidence are sufficient and appropriate and reflect the work performed in relation to significant judgements. Have we confirmed that they support the conclusions reached?",
  },
  {
    id: "EQR-8",
    text: "Have you reviewed the appropriateness of the report issued and whether there are any changes from the expected form and content of the report from the planning stage?",
  },
  {
    id: "EQR-9",
    text: "Have you conducted the review in a timely manner at appropriate stages during the engagement so that significant matters may be promptly resolved to the reviewer's satisfaction before the report is issued?",
  },
  {
    id: "EQR-10",
    text: "Have there been any instances which indicate that the objectivity of the engagement quality control reviewer has been impacted. For example, the engagement quality control reviewer:\n- Is not selected by the engagement partner;\n- Does not otherwise participate in the engagement during the period of review;\n- Does not make decisions for the engagement team; and\n- Is not subject to other considerations that would threaten the reviewer's objectivity.",
  },
  {
    id: "EQR-11",
    text: "Where the engagement partner has consulted the engagement quality control reviewer, have we ensured that the objectivity of the reviewer is not compromised? Where this is not possible, another individual within the firm or a suitably qualified external person is appointed to take on the role of either the engagement quality control reviewer or the person to be consulted on the engagement.",
  },
  {
    id: "EQR-12",
    text: "You have not become aware of any unresolved matters that would cause you to believe that the significant judgments the engagement team made and the conclusions they reached were not appropriate.",
  },
  {
    id: "EQR-13",
    text: "Have you ensured that the engagement quality control review has been completed before the report is issued?",
  },
] as const;

type EqrChecklistState = Record<string, boolean>;

interface EqrChecklistProps {
  setActiveSection?: (section: string) => void;
}

export function EqrChecklist({ setActiveSection }: EqrChecklistProps = {}) {
  const [confirmed, setConfirmed] = useState<EqrChecklistState>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "error">("idle");

  const loadResponses = useCallback(async () => {
    setLoading(true);
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson(EQR_REVIEW_FILE);
        if (result?.success && result.data?.checklist) {
          const checklist = result.data.checklist as Record<string, boolean>;
          const next: EqrChecklistState = {};
          EQR_QUESTIONS.forEach((q) => {
            next[q.id] = !!checklist[q.id];
          });
          setConfirmed(next);
        } else {
          EQR_QUESTIONS.forEach((q) => {
            setConfirmed((prev) => ({ ...prev, [q.id]: false }));
          });
        }
      } else {
        EQR_QUESTIONS.forEach((q) => {
          setConfirmed((prev) => ({ ...prev, [q.id]: false }));
        });
      }
    } catch (error) {
      console.error("Failed to load EQRReview.json", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  const saveResponses = useCallback(
    async (nextConfirmed: EqrChecklistState) => {
      setSaving(true);
      setSaveStatus("idle");
      try {
        let existingData: Record<string, unknown> = {};
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const res = await (window as any).sharePointAPI.downloadClientJson(EQR_REVIEW_FILE);
          if (res?.success && res.data && typeof res.data === "object") {
            existingData = res.data as Record<string, unknown>;
          }
        }

        const checklist: Record<string, boolean> = {};
        EQR_QUESTIONS.forEach((q) => {
          checklist[q.id] = !!nextConfirmed[q.id];
        });

        const payload = {
          ...existingData,
          checklist,
        };

        if ((window as any).sharePointAPI?.uploadClientJson) {
          const uploadRes = await (window as any).sharePointAPI.uploadClientJson(
            EQR_REVIEW_FILE,
            payload
          );
          if (uploadRes?.success) {
            setSaveStatus("saved");
          } else {
            setSaveStatus("error");
          }
        } else {
          setSaveStatus("error");
        }
      } catch (error) {
        console.error("Failed to save EQRReview.json", error);
        setSaveStatus("error");
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const handleConfirmedChange = (questionId: string, checked: boolean) => {
    const next = { ...confirmed, [questionId]: checked };
    setConfirmed(next);
    saveResponses(next);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6 flex items-center justify-center">
        <div className="flex items-center gap-2 text-white/70">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading EQR checklist...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/30 to-purple-600/20 flex items-center justify-center">
                  <ClipboardCheck className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">EQR Checklist</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Checklist for EQR procedures</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {saving && (
                  <span className="text-xs text-white/50 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Saving...
                  </span>
                )}
                {saveStatus === "saved" && !saving && (
                  <span className="text-xs text-green-400 flex items-center gap-1">Saved</span>
                )}
                {saveStatus === "error" && !saving && (
                  <span className="text-xs text-red-400">Save failed</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {EQR_QUESTIONS.map((q, index) => (
              <div
                key={q.id}
                className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3"
              >
                <div className="flex gap-3">
                  <span className="text-sm font-medium text-white/60 shrink-0">{index + 1}.</span>
                  <p className="text-sm text-white/90 whitespace-pre-line flex-1">{q.text}</p>
                </div>
                <div className="flex items-center gap-2 pl-6">
                  <Checkbox
                    id={q.id}
                    checked={!!confirmed[q.id]}
                    onCheckedChange={(c) => handleConfirmedChange(q.id, c === true)}
                    className="border-white/20 data-[state=checked]:bg-[#4da3ff] data-[state=checked]:border-[#4da3ff]"
                  />
                  <Label
                    htmlFor={q.id}
                    className="text-sm text-white/80 cursor-pointer font-medium"
                  >
                    Confirmed
                  </Label>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
